import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BiSearch } from 'react-icons/bi';
import tenderAPI from '../../services/tenderAPI';

const WorkspaceHeaderSearch = ({ pages = [] }) => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tenders, setTenders] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const pageHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 1) return [];
    return pages
      .filter((page) => `${page.label} ${page.path}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [pages, query]);

  const results = useMemo(() => {
    const rows = pageHits.map((page) => ({
      kind: 'page',
      id: page.path,
      title: page.label,
      meta: 'Page',
      path: page.path
    }));
    tenders.forEach((tender) => {
      const id = tender.id || tender._id;
      rows.push({
        kind: 'tender',
        id: String(id),
        title: tender.title || tender.reference || 'Tender',
        meta: tender.reference || tender.organization || 'Tender',
        path: `/apps/tender/register/${id}`
      });
    });
    return rows;
  }, [pageHits, tenders]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setTenders([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      tenderAPI
        .getTenders({ search: q, limit: 6 })
        .then((body) => {
          if (cancelled) return;
          const list = body?.data?.tenders || body?.tenders || [];
          setTenders(list.slice(0, 6));
        })
        .catch(() => {
          if (!cancelled) setTenders([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, results.length]);

  useEffect(() => {
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, []);

  const go = (row) => {
    if (!row?.path) return;
    setOpen(false);
    setQuery('');
    navigate(row.path);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault();
      go(results[activeIndex]);
      return;
    }
    if (!results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    }
  };

  const showPanel = open && query.trim().length > 0;

  return (
    <div className="workspace-header-search" ref={rootRef}>
      <label className="workspace-header-search__field">
        <BiSearch className="workspace-header-search__icon" aria-hidden />
        <input
          ref={inputRef}
          type="search"
          value={query}
          placeholder="Search tenders and pages"
          aria-label="Search tenders and pages"
          aria-expanded={showPanel}
          aria-controls="workspace-header-search-results"
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </label>
      {showPanel ? (
        <div id="workspace-header-search-results" className="workspace-header-search__panel" role="listbox">
          {results.length ? results.map((row, index) => (
            <button
              key={`${row.kind}-${row.id}`}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={`workspace-header-search__hit ${index === activeIndex ? 'is-active' : ''}`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => go(row)}
            >
              <span className="workspace-header-search__hit-title">{row.title}</span>
              <span className="workspace-header-search__hit-meta">{row.meta}</span>
            </button>
          )) : (
            <p className="workspace-header-search__empty">
              {loading ? 'Searching…' : 'No matches'}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default WorkspaceHeaderSearch;
